import { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../legacy-ball/table-management/designTokens';

interface GuestPassSettings {
  eventId: string;
  eventName: string;
  guestPassEnabled: boolean;
  guestPassSlug: string | null;
  subscriberCount: number;
}

const PASS_BASE_URL = typeof window !== 'undefined' ? `${window.location.origin}/pass` : 'https://kduhub.com/pass';

export function GuestPassPanel({ eventId }: { eventId: string }) {
  const [settings, setSettings] = useState<GuestPassSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [toast, setToast] = useState('');
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    loadSettings();
  }, [eventId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await apiClient<GuestPassSettings>(`/guest-pass/event/${eventId}/settings`);
      setSettings(data);
      setEditSlug(data.guestPassSlug || '');
    } catch {
      setError('Failed to load GuestPass settings.');
    } finally {
      setLoading(false);
    }
  };

  // Render QR code when slug changes
  useEffect(() => {
    if (!settings?.guestPassSlug || !qrCanvasRef.current) return;
    const url = `${PASS_BASE_URL}/${settings.guestPassSlug}`;
    QRCode.toCanvas(qrCanvasRef.current, url, {
      width: 240,
      margin: 2,
      color: { dark: '#1a6b3c', light: '#ffffff' },
    }).catch(() => {});
  }, [settings?.guestPassSlug]);

  const handleToggle = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const result = await apiClient<any>(`/guest-pass/event/${eventId}/settings`, {
        method: 'PATCH',
        body: { guestPassEnabled: !settings.guestPassEnabled },
      });
      setSettings((prev) => prev ? { ...prev, guestPassEnabled: result.guestPassEnabled, guestPassSlug: result.guestPassSlug } : prev);
      setEditSlug(result.guestPassSlug || '');
      setToast(result.guestPassEnabled ? 'GuestPass enabled!' : 'GuestPass disabled.');
      setTimeout(() => setToast(''), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  const handleSlugSave = async () => {
    if (!editSlug.trim()) return;
    setSaving(true);
    try {
      const result = await apiClient<any>(`/guest-pass/event/${eventId}/settings`, {
        method: 'PATCH',
        body: { guestPassSlug: editSlug.trim() },
      });
      setSettings((prev) => prev ? { ...prev, guestPassSlug: result.guestPassSlug } : prev);
      setEditSlug(result.guestPassSlug || '');
      setToast('Slug updated!');
      setTimeout(() => setToast(''), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update slug.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrCanvasRef.current || !settings?.guestPassSlug) return;
    const link = document.createElement('a');
    link.download = `guestpass-${settings.guestPassSlug}.png`;
    link.href = qrCanvasRef.current.toDataURL('image/png');
    link.click();
  };

  const handlePrintQR = () => {
    if (!settings?.guestPassSlug || !qrCanvasRef.current) return;
    const url = `${PASS_BASE_URL}/${settings.guestPassSlug}`;
    const qrDataUrl = qrCanvasRef.current.toDataURL('image/png');
    const html = `<!DOCTYPE html><html><head><title>GuestPass QR — ${settings.eventName}</title>
    <style>
      body { font-family: sans-serif; text-align: center; padding: 60px 20px; }
      h1 { font-size: 22px; color: #1a6b3c; margin-bottom: 4px; }
      h2 { font-size: 28px; margin: 8px 0; }
      p { color: #666; font-size: 14px; margin: 4px 0; }
      .qr { margin: 30px auto; }
      .qr img { width: 300px; height: 300px; border: 3px solid #1a6b3c; border-radius: 8px; }
      .url { font-family: monospace; font-size: 12px; color: #999; margin-top: 20px; }
      .brand { font-size: 11px; color: #aaa; margin-top: 30px; }
    </style>
    </head><body>
      <h1>KDU Georgia 2026</h1>
      <h2>${settings.eventName}</h2>
      <p>Scan to view event details</p>
      <div class="qr"><img src="${qrDataUrl}" alt="QR Code" /></div>
      <p class="url">${url}</p>
      <p class="brand">Powered by KDUHub GuestPass</p>
      <script>setTimeout(function() { window.print(); }, 300);</script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED }}>Loading GuestPass settings...</div>;
  if (error && !settings) return <div style={{ padding: '2rem', color: '#d32f2f' }}>{error}</div>;
  if (!settings) return null;

  const passUrl = settings.guestPassSlug ? `${PASS_BASE_URL}/${settings.guestPassSlug}` : null;

  return (
    <div>
      {toast && (
        <div style={{ position: 'fixed', top: '16px', right: '16px', background: '#1a6b3c', color: '#fff', padding: '10px 20px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: TEXT_DARK, margin: '0 0 4px' }}>🎫 GuestPass Settings</h3>
        <p style={{ fontSize: '0.85rem', color: TEXT_MUTED, margin: 0 }}>
          Enable a public, account-free page for this event. Guests scan a QR code to access event details.
        </p>
      </div>

      {/* Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '12px', marginBottom: '20px' }}>
        <button
          onClick={handleToggle}
          disabled={saving}
          style={{
            width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer', position: 'relative',
            backgroundColor: settings.guestPassEnabled ? PRIMARY : '#d1d5db',
            transition: 'background-color 0.2s',
          }}
          aria-label={`GuestPass ${settings.guestPassEnabled ? 'enabled' : 'disabled'}`}
          aria-pressed={settings.guestPassEnabled}
        >
          <span style={{
            position: 'absolute', top: '3px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            left: settings.guestPassEnabled ? '25px' : '3px', transition: 'left 0.2s',
          }} />
        </button>
        <div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>
            GuestPass is {settings.guestPassEnabled ? 'Enabled' : 'Disabled'}
          </div>
          <div style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>
            {settings.guestPassEnabled ? 'Guests can access event info via QR code' : 'Enable to generate a public event page'}
          </div>
        </div>
      </div>

      {settings.guestPassEnabled && (
        <>
          {/* Slug editor */}
          <div style={{ padding: '16px', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '12px', marginBottom: '20px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
              Event URL Slug
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>/pass/</span>
              <input
                type="text"
                value={editSlug}
                onChange={(e) => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                style={{ flex: 1, padding: '10px 12px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'monospace' }}
                placeholder="event-slug"
                aria-label="GuestPass URL slug"
              />
              <button
                onClick={handleSlugSave}
                disabled={saving || editSlug === settings.guestPassSlug}
                style={{ padding: '10px 16px', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', opacity: editSlug === settings.guestPassSlug ? 0.5 : 1 }}
              >
                Save
              </button>
            </div>
            {passUrl && (
              <p style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginTop: '8px' }}>
                Full URL: <a href={passUrl} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY }}>{passUrl}</a>
              </p>
            )}
          </div>

          {/* QR Code + Actions */}
          {settings.guestPassSlug && (
            <div style={{ padding: '24px', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '12px', marginBottom: '20px', textAlign: 'center' }}>
              <canvas ref={qrCanvasRef} style={{ display: 'block', margin: '0 auto 16px', borderRadius: '8px', border: `3px solid ${PRIMARY}` }} />
              <p style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '4px' }}>{settings.eventName}</p>
              <p style={{ fontSize: '0.72rem', color: TEXT_MUTED, marginBottom: '16px' }}>Scan to view event details</p>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={handleDownloadQR} style={{ padding: '10px 18px', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  ⬇️ Download PNG
                </button>
                <button onClick={handlePrintQR} style={{ padding: '10px 18px', backgroundColor: '#f3f4f6', color: TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  🖨️ Print QR
                </button>
              </div>
            </div>
          )}

          {/* Subscriber count */}
          <div style={{ padding: '16px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>📬 Subscribers</div>
                <div style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>People opted in for event updates</div>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: PRIMARY }}>{settings.subscriberCount}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
